import {useState, useRef} from 'react';
import {useRequest} from 'ahooks';

const defaultState = {
  optimization: false,
  optimizer_runs: 200,
  contract_library_address_list: [],
  contract_code: '',
  contract_name: '',
  evm_version: 'default',
  abi: '',
};

function BasicInfo({onChange, compilerVersionList, compilerVersion, contractAddress}) {
  return (
    <div className="row mb-4">
      <div className="js-form-message col-md-5 mb-3 mb-md-0">
        <label htmlFor="txtContractAddress">Contract Address </label>
        <input type="text" value={contractAddress} maxLength="42" readOnly="readonly" className="form-control form-control-sm" placeholder="0x..." data-name="txtContractAddress" />
      </div>
      <div className="js-form-message col-md-5 mb-3 mb-md-0">
        <label htmlFor="ddlCompilerVersions">Compiler</label>
        <div className="js-focus-state input-group input-group-sm mb-2">
          <select disabled="disabled" className="aspNetDisabled custom-select custom-select-sm" defaultValue={compilerVersion}>
            <option value="">[Please Select]</option>
            {compilerVersionList.map(item => {
              return (
                <option key={item} value={item}>
                  {item}
                </option>
              );
            })}
          </select>
        </div>
      </div>
      <div className="col-md mb-3 mb-md-0">
        <i
          className="far fa-question-circle text-muted"
          data-toggle="tooltip"
          data-html="true"
          data-title="<p className='text-white text-left'>Select the option you used when compiling this contract</p>"
          data-original-title=""
          title=""
        ></i>
        <label htmlFor="ddlOptimization">Optimization</label>
        <div className="js-focus-state input-group input-group-sm mb-2">
          <select defaultValue={false} className="custom-select custom-select-sm" onChange={onChange}>
            <option value={true}>Yes</option>
            <option value={false}>No</option>
          </select>
        </div>
      </div>
    </div>
  );
}

function ConstructorArguments({onChange}) {
  const [expand, setExpand] = useState(true);
  return (
    <div className="card mb-3">
      <div className="card-header card-collapse">
        <h5 className="mb-0">
          <button
            type="button"
            className="btn btn-link btn-block d-flex justify-content-between card-btn p-3"
            data-toggle="collapse"
            data-target="#basicsCollapseOne"
            aria-expanded={String(expand)}
            aria-controls="basicsCollapseOne"
            onClick={() => {
              setExpand(!expand);
            }}
          >
            <label>
              Constructor Arguments{' '}
              <a href="https://solidity.readthedocs.io/en/develop/abi-spec.html" target="_blank" rel="nofollow noopener">
                ABI-encoded
              </a>{' '}
              <span className="text-secondary">(for contracts that were created with constructor parameters)</span>
            </label>
            <span className="card-btn-arrow">
              <span className="fas fa-arrow-down small"></span>
            </span>
          </button>
        </h5>
      </div>
      <div className={`collapse ${expand ? 'show' : ''}`} aria-labelledby="basicsHeadingOne" data-parent="#basicsAccordion">
        <div className="card-body">
          <textarea
            rows="3"
            cols="20"
            className="form-control form-control-sm w-100 mb-2"
            spellCheck="false"
            data-toggle="tooltip"
            title=""
            data-original-title="This option ONLY applies to contracts that accept constructor arguments, if you are unsure you most likely dont need this. The constructor arguments should be in ABI-ENCODED values and will be appended to the contract bytecode."
            onChange={e => {
              onChange('abi', e.target.value);
            }}
          ></textarea>
          <p className="font-size-1 mb-0">
            For additional information on Constructor Arguments{' '}
            <a href="https://docs.movachain.com/contract-verification-constructor-arguments/" target="_blank">
              see Our KB Entry <i className="fas fa-external-link-alt"></i>
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

function ContractLibraryAddress({onChange}) {
  const [expand, setExpand] = useState(false);
  const [extralibraries, setExtralibraries] = useState([{}, {}, {}, {}, {}, {}, {}, {}, {}, {}]);
  return (
    <div className="card mb-3">
      <div className="card-header card-collapse">
        <h5 className="mb-0">
          <button
            type="button"
            className="btn btn-link btn-block d-flex justify-content-between card-btn collapsed p-3"
            data-toggle="collapse"
            data-target="#basicsCollapseTwo"
            aria-expanded={String(expand)}
            aria-controls="basicsCollapseTwo"
            onClick={() => {
              setExpand(!expand);
            }}
          >
            <label>
              Contract Library Address <span className="text-secondary">(for contracts that use libraries, supports up to 10 libraries)</span>
            </label>
            <span className="card-btn-arrow">
              <span className="fas fa-arrow-down small"></span>
            </span>
          </button>
        </h5>
      </div>
      <div className={`collapse ${expand ? 'show' : ''}`} aria-labelledby="basicsHeadingTwo" data-parent="#basicsAccordion">
        <div className="card-body">
          <div className="col-md-12">
            <span className="text-secondary">
              <b>Note: Library names are case sensitive and affects the keccak library hash</b>
            </span>
            <br />
            <br />
            {extralibraries.map((item, index) => {
              return (
                <div className="d-md-flex mb-3" key={index}>
                  <div className="form-inline mr-md-3 mb-2 mb-md-0">
                    <label className="text-monospace text-secondary mr-sm-3 mb-1">Library_1 Name:</label>
                    <input
                      type="text"
                      maxLength="42"
                      className="form-control form-control-xss w-100"
                      data-toggle="tooltip"
                      title=""
                      rel="tooltip"
                      data-placement="top"
                      data-original-title="This option ONLY applies to contracts that use libraries. The source code should contain the concatenated contract and library source code in a single page"
                      onChange={e => {
                        const extralibs = [...extralibraries];
                        extralibs[index] = {
                          ...extralibraries[index],
                          name: e.target.value,
                        };
                        setExtralibraries(extralibs);
                        onChange(extralibs);
                      }}
                    />
                  </div>
                  <div className="d-none d-md-block align-self-end mr-md-3 mb-1">
                    <span className="btn btn-xs btn-icon btn-soft-secondary rounded-circle">
                      <i className="fa fa-long-arrow-alt-right btn-icon__inner"></i>
                    </span>
                  </div>
                  <div className="form-inline">
                    <label className="text-monospace text-secondary mr-sm-2 mb-1">Library_1 Contract Address:</label>
                    <input
                      maxLength="42"
                      className="form-control form-control-xss w-100"
                      data-toggle="tooltip"
                      title=""
                      rel="tooltip"
                      data-placement="top"
                      type="search"
                      placeholder="0x..."
                      data-original-title="Corresponding Contract Library Address"
                      onChange={e => {
                        const extralibs = [...extralibraries];
                        extralibs[index] = {
                          ...extralibraries[index],
                          address: e.target.value,
                        };
                        setExtralibraries(extralibs);
                        onChange(extralibs);
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function MiscSettings({evmVersionList, compilerLicenseList, licenseType, onChange}) {
  const [expand, setExpand] = useState(false);
  return (
    <div className="card mb-3">
      <div className="card-header card-collapse">
        <h5 className="mb-0">
          <button
            type="button"
            className="btn btn-link btn-block d-flex justify-content-between card-btn collapsed p-3"
            data-toggle="collapse"
            data-target="#basicsCollapseThree"
            aria-expanded={String(expand)}
            aria-controls="basicsCollapseThree"
            onClick={() => {
              setExpand(!expand);
            }}
          >
            <label>
              Misc Settings <span className="text-secondary">(Runs, EvmVersion &amp; License Type settings)</span>
            </label>
            <span className="card-btn-arrow">
              <span className="fas fa-arrow-down small"></span>
            </span>
          </button>
        </h5>
      </div>
      <div className={`collapse ${expand ? 'show' : ''}`} aria-labelledby="basicsHeadingThree" data-parent="#basicsAccordion">
        <div className="card-body">
          <div className="row mb-4">
            <div className="col-md-4 mb-2">
              <i
                className="far fa-question-circle text-muted"
                data-toggle="tooltip"
                data-html="true"
                data-title="<p className='text-white text-left'>(Applicable when Optimization = Yes) <br />Optimize for how many times you intend to run the code. Lower values will optimize more for initial deployment cost, higher values will optimize more for high-frequency usage.</p>"
                data-original-title=""
                title=""
              ></i>
              <label htmlFor="txtRuns">
                Runs <span className="d-inline-block d-md-none d-lg-inline-block">(Optimizer)</span>
              </label>
              <input
                type="text"
                maxLength="8"
                className="form-control form-control-sm"
                required=""
                data-rule-maxlength="8"
                title=""
                defaultValue={200}
                data-success-class="u-has-success"
                data-rule-minlength="1"
                data-error-class="u-has-error"
                data-msg-minlength="Invalid Length"
                data-msg="Required"
                data-toggle="tooltip"
                data-original-title="Do not change if you are unsure. Previous versions of truffle defaulted to a value of 0"
                onChange={e => {
                  onChange('optimizer_runs', e.target.value);
                }}
              />
              <span style={{visibility: 'hidden'}}></span>
              <span style={{visibility: 'hidden'}}></span>
            </div>
            <div className="col-md-4">
              <i
                className="far fa-question-circle text-muted"
                data-toggle="tooltip"
                data-html="true"
                data-title="<p className='text-white text-left'>When you compile your contract code you can specify the Mova virtual machine version to compile for to avoid particular features or behaviours.</p>"
                data-original-title=""
                title=""
              ></i>
              <label>EVM Version to target</label>
              <select
                className="custom-select custom-select-sm mb-1"
                data-toggle="tooltip"
                title=""
                data-original-title="A list of target EVM versions and the compiler-relevant changes introduced at each version. Backward compatibility is not guaranteed between each version."
                onChange={e => {
                  onChange('evm_version', e.target.value);
                }}
              >
                {evmVersionList.map(item => {
                  return (
                    <option value={item.evm_version} key={item.evm_version}>
                      {item.evm_version} ({item.info})
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="col-md-4">
              <label>
                LicenseType{' '}
                <a href="/contract-license-types" target="_blank" data-toggle="tooltip" title="" data-original-title="Contract Source Code License Type, click for more info">
                  <i className="far fa-info-circle"></i>
                </a>
              </label>
              <select
                title="Select a suitable license type"
                className="custom-select custom-select-sm"
                required=""
                data-msg="Required"
                data-error-class="u-has-error"
                data-success-class="u-has-success"
                defaultValue={licenseType}
                onChange={e => {
                  onChange('license_type', e.target.value);
                }}
              >
                {compilerLicenseList.map(item => {
                  return (
                    <option key={item.liscense_type} value={item.liscense_type}>
                      {item.liscense_type}({item.symbol})
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SuccessResult({result, contractDetail, query}) {
  return (
    <div className="tab-pane fade active show">
      <h4 className="h5 mb-1"></h4>
      <p className="ml-1">
        <b>Compiler debug log:</b>
        <br />
        <span>
          <i className="fa fa-check-circle"></i> Note: Contract was created during TxHash#{' '}
          <span className="text-primary">
            <a href={`/tx/${result.data.results.txhash}`}>{result.data.results.txhash}</a>
            {/* <a href="/tx/0xbe10fa4a9d7239b146b9ce244b8356cec9dca30b8d8297c74b402dce3f1bd2b1">{result.data.results.txhash}</a> */}
          </span>
          <br />
        </span>
        <span>
          <i className="fa fa-thumbs-up mr-1"></i>
          <span className="text-success">Successfully generated ByteCode and ABI for Contract Address </span>
          <span className="text-primary">
            <a href={`/address/${result.data.results.contract_address}`}>[{result.data.results.contract_address}]</a>
            {/* <a href="/address/0xDf699C02cbF22eD25B36c7D791c65350bbC02B76#code">[{result.data.results.contract_address}]</a> */}
          </span>
          <br />
          <br />
        </span>
      </p>
      <p></p>
      <br />
      <p></p>
    </div>
  );
}

function ErrorResult({result, query, onClick}) {
  return (
    <div className="tab-pane fade active show">
      <h4 className="h5 mb-1"></h4>
      <p className="ml-1">
        <b>Compiler debug log:</b>
        <br />
        <span></span>
        <span>
          <span className="text-danger">
            <i className="fa fa-ban"></i>
            {result.enMsg}
          </span>
          {/* <br /> */}
          {/* <i className="fa fa-angle-double-right"></i> Found the following ContractName(s) in source code : <b>ERC20Interface</b>, <b>MyToken</b>, <b>Ownable</b>, <b>SafeMath</b> */}
          {/* <br /> */}
          {/* <i className="fa fa-angle-double-right"></i> But we were unable to locate a matching bytecode (err_code_2) */}
          <br />
          <i className="fa fa-info-circle text-secondary"></i> For troubleshooting, you can try compiling your source code with the{' '}
          <span className="text-primary">
            <a href="https://remix.ethereum.org" target="_blank">
              Remix - Solidity IDE
            </a>
          </span>{' '}
          and check for exceptions
          <br />
          <br />
        </span>
      </p>
      <p></p>
      <br />
      <p>
        <a className="btn btn-primary btn-sm" href="#" onClick={onClick}>
          Start Over
        </a>
      </p>
    </div>
  );
}

export default function StepTwo({stepOneData, compilerLicenseList, evmVersionList, compilerVersionList, onBack}) {
  const [state, setState] = useState({
    ...stepOneData,
    ...defaultState,
  });
  const [result, setResult] = useState();
  const [contractDetail, setContractDetail] = useState();
  const multiFilesRef = useRef();

  const contractVerifyRequest = useRequest(
    body => ({
      url: '/blockBrowser/contract/contractVerify',
      method: 'POST',
      body: JSON.stringify(body),
    }),
    {manual: true, formatResult: res => res},
  );
  const contractQueryByAddressRequest = useRequest(
    body => ({
      url: '/blockBrowser/contract/contractQueryByAddress',
      method: 'POST',
      body: body, // {contract_address: ''}
    }),
    {manual: true},
  );

  const uploadContractFileRequest = useRequest(
    data => ({
      url: '/blockBrowser/contract/uploadContractFile',
      method: 'POST',
      body: data, // {contract_address: ''}
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
    {manual: true},
  );

  const handleUploadFiles = () => {
    const files = multiFilesRef.current?.files;

    if (!files) {
      return;
    }

    var form = new FormData();

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      form.append('file', file, file.name);
    }

    var settings = {
      url: '/blockBrowser/contract/uploadContractFile',
      method: 'POST',
      timeout: 0,
      processData: false,
      mimeType: 'multipart/form-data',
      contentType: false,
      data: form,
    };

    window.$.ajax(settings).done(function (res) {
      try {
        const data = JSON.parse(res).data;
        setState({...state, contract_name: data.contract_name, contract_code: data.contract_code});
      } catch (error) {
        console.log(error);
      }
    });

    // uploadContractFileRequest.run(formdata).then(res => {
    //   console.log(res);
    //   setState({...state, contract_name: res.contract_name, contract_code: res.contract_code});
    // });
  };

  const handleConfirmStepTwo = e => {
    const {optimization, chk_terms, contract_library_address_list, optimizer_runs, ...restState} = state;

    contractVerifyRequest
      .run({
        ...restState,
        // contract_name: 'SimpleStorage',
        optimization: optimization === 'true',
        contract_library_address_list: contract_library_address_list.filter(c => c.name && c.address),
        optimizer_runs: Number(optimizer_runs) || 200,
      })
      .then(data => {
        setResult(data);

        if (data.code === 0) {
          contractQueryByAddressRequest.run({address: state.contract_address}).then(res => setContractDetail(res));
        }
      });
  };

  return (
    <main role="main">
      <div className="container space-top-1 space-bottom-2">
        <div style={{display: 'none'}}></div>
        <div className="text-center mb-4">
          <div className="w-md-75 w-lg-75 mx-md-auto ">
            <h1 className="h4">Verify &amp; Publish Contract Source Code </h1>
            <span className="badge badge-success py-2" style={{borderRadius: '5rem', paddingLeft: '15px', paddingRight: '15px'}}>
              Compiler Type: SINGLE FILE / CONCATENANTED METHOD
            </span>
          </div>
        </div>
        <div className="container">
          <div className="border-top py-1">
            <p className="mb-0">
              <span className="d-none d-md-inline-block u-label u-label--price rounded mt-1 ml-n1">Info:</span> A simple and structured interface for verifying smart contracts that fit in a single
              file
            </p>
          </div>
        </div>
        <form method="post" className="js-validate mt-2" autoComplete="off">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center p-0">
              <ul className="nav nav-custom nav-borderless nav-tabs1" role="tablist">
                <li className="nav-item">
                  <a className={`nav-link ${!result ? 'active' : ''}`} data-toggle="tab" href="#contractsource" role="tab" aria-controls="overview" aria-selected="true">
                    Contract Source Code
                  </a>
                </li>
                {result ? (
                  <li className="nav-item">
                    <a className="nav-link active" data-toggle="tab" href="#bytecode" role="tab" aria-controls="settings" aria-selected="true">
                      Compiler Output
                    </a>
                  </li>
                ) : undefined}
              </ul>
              <div className="mr-3">
                <span style={{display: 'none'}}>... Processing, this can take up to 30 seconds ...</span>
              </div>
            </div>
            <div className="card-body">
              <div className="tab-content py-1 ml-1 mr-1">
                <div className={`tab-pane fade ${result ? '' : 'show active'}`}>
                  <span></span>
                  <div>
                    <div className="alert alert-dark alert-dismissible fade show font-size-1" style={{backgroundColor: '#E9ECF1'}}>
                      <button type="button" className="close" data-dismiss="alert" aria-label="Close">
                        <span aria-hidden="true">×</span>
                      </button>
                      <ul className="list-unstyled list-xs-space alert-text mb-0 mt-2">
                        <li>
                          1. If the contract compiles correctly at{' '}
                          <a href="https://remix.ethereum.org/" target="_blank" rel="nofollow noopener">
                            REMIX
                          </a>
                          , it should also compile correctly here.
                        </li>
                        <li>2. We have limited support for verifying contracts created by another contract and there is a timeout of up to 45 seconds for each contract compiled. </li>
                        <li>
                          3. For programatic contract verification, check out the{' '}
                          <a href="apis#contracts" target="_blank">
                            Contract API Endpoint
                          </a>
                        </li>
                      </ul>
                    </div>
                    <BasicInfo
                      compilerVersionList={compilerVersionList}
                      contractAddress={stepOneData.contract_address}
                      compilerVersion={stepOneData.compiler_version}
                      onChange={e => {
                        setState({...state, optimization: e.target.value});
                      }}
                    />
                    {state.compiler_type === 'MultiFile' ? (
                      <div className="row mb-4">
                        <div className="col col-md-12 col-sm-12">
                          <br />
                          <div className="card">
                            <div className="mt-3 mr-2 ml-3 js-form-message form-group">
                              <label for="txtSourceCode" className="d-block">
                                <b>Please select the Solidity (*.sol) files for upload</b> <span className="text-danger">*</span>
                                <br />
                              </label>
                              Step 1:
                              <input type="file" multiple="multiple" title="CTRL click to select Multiple files" accept=".sol" ref={multiFilesRef} />
                              <br />
                              <br />
                              Step 2:
                              <input type="button" value="Click to Upload selected files" className="btn btn-secondary btn-xs" onClick={handleUploadFiles} />
                              <span></span>
                              <div className="mt-4"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="js-form-message mb-4">
                        <label htmlFor="txtSourceCode" className="d-block">
                          <b>Enter the Solidity Contract Code below</b> <span className="text-danger">*</span>
                        </label>
                        <textarea
                          rows="10"
                          cols="20"
                          className="form-control form-control-sm w-100 mb-1"
                          spellCheck="false"
                          required=""
                          data-msg="Required"
                          data-error-class="u-has-error"
                          data-success-class="u-has-success"
                          onChange={e => {
                            setState({...state, contract_code: e.target.value});
                          }}
                        ></textarea>
                        <span style={{visibility: 'hidden'}}></span>
                      </div>
                    )}
                    <div>
                      <ConstructorArguments
                        onChange={(type, e) => {
                          setState({...state, [type]: e});
                        }}
                      />
                      <ContractLibraryAddress
                        onChange={e => {
                          setState({...state, contract_library_address_list: e});
                        }}
                      />
                      <MiscSettings
                        licenseType={stepOneData.license_type}
                        compilerLicenseList={compilerLicenseList}
                        evmVersionList={evmVersionList}
                        onChange={(type, e) => {
                          setState({...state, [type]: e});
                        }}
                      />
                    </div>
                    <div className="text-center mb-4">
                      <input
                        type="button"
                        value="Verify and Publish"
                        className="btn btn-primary mr-2 btn-submit"
                        data-toggle="tooltip"
                        title=""
                        data-original-title="Submit source code for verification"
                        onClick={handleConfirmStepTwo}
                      />
                      &nbsp;
                      <input
                        type="button"
                        value="Reset"
                        title="Reset this page"
                        className="btn btn-soft-secondary"
                        onClick={() => {
                          setState({...state, ...defaultState});
                        }}
                      />
                      &nbsp;
                      <input type="button" value="Return to Main" title="Return to selection menu, Re-Start from the beginning" className="btn btn-soft-secondary" onClick={onBack} />
                    </div>
                  </div>
                </div>
                {result ? (
                  result.code !== 0 ? (
                    <ErrorResult
                      result={result}
                      query={state}
                      onClick={() => {
                        setResult(undefined);
                      }}
                    />
                  ) : (
                    <SuccessResult result={result} query={state} contractDetail={contractDetail} />
                  )
                ) : undefined}
              </div>
            </div>
          </div>
        </form>
      </div>
    </main>
  );
}
